import { useMemo, useState } from 'react';
import {
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiBookmark3Line,
  RiFlashlightLine,
  RiNodeTree,
  RiPinDistanceLine,
  RiPushpin2Line,
  RiShieldKeyholeLine,
  RiSparkling2Line,
  RiTimeLine,
} from 'react-icons/ri';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Hint } from '../ui/tooltip';
import { useCanvasStore } from '../../canvas/canvasStore';
import { useStore } from '../../state/store';
import {
  CLASSIFICATION_LABEL,
  PRIORITY_LABEL,
  TIMELINE_KIND_LABEL,
} from '../../state/domain';
import { suggestLinks, unplacedEntities, type LinkSuggestion } from '../../canvas/linkSuggest';
import { RISK_BG } from '../icons';
import { cn, formatDate, formatDay } from '@/lib/utils';

type Controller = NonNullable<ReturnType<typeof useCanvasStore.getState>['controller']>;

/* ── 案件名牌（钉在墙面左上）─────────────────────────── */
export function WallCasePlate() {
  const engagement = useStore(state =>
    state.engagements.find(e => e.id === state.activeEngagementId)
  );
  const targets = useStore(state => state.targets);
  const entities = useStore(state => state.entities);
  const controller = useCanvasStore(state => state.controller);
  const revision = useCanvasStore(state => state.revision);

  const wallStats = useMemo(() => {
    void revision;
    if (!controller) return { cards: 0, ropes: 0, pins: 0 };
    const all = controller.surface.all();
    return {
      cards: all.filter(el => el.type === 'note' || el.type === 'photo').length,
      ropes: all.filter(el => el.type === 'rope' || el.type === 'connector').length,
      pins: all.filter(el => el.type === 'pin').length,
    };
  }, [controller, revision]);

  if (!engagement) return null;

  const topRisk = targets.length
    ? targets.reduce((a, b) => (b.riskScore > a.riskScore ? b : a))
    : null;

  return (
    <div className="pointer-events-auto absolute left-[68px] top-3 z-10 w-[298px]">
      <div className="relative rounded-[var(--radius)] border border-border bg-card/96 p-3 pl-4 shadow-[0_12px_36px_rgba(27,28,30,0.16)] backdrop-blur">
        <div className="absolute -top-2 left-1/2 grid size-5 -translate-x-1/2 place-items-center rounded-full border border-[#7f1d1d] bg-[var(--color-cord)] shadow-md">
          <span className="size-1.5 rounded-full bg-white/70" />
        </div>

        <div className="flex items-center gap-1.5">
          <RiShieldKeyholeLine className="size-3.5 text-muted-foreground" />
          <span className="font-mono text-[10px] tracking-wider text-muted-foreground">
            {engagement.caseNo ?? '—'}
          </span>
          <Badge
            variant={
              engagement.classification === 'secret' ||
              engagement.classification === 'confidential'
                ? 'cord'
                : 'outline'
            }
            className="ml-auto"
          >
            {CLASSIFICATION_LABEL[engagement.classification]}
          </Badge>
        </div>

        <h2 className="mt-1.5 text-[13.5px] font-semibold leading-snug">{engagement.name}</h2>

        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground">
          <span>主办 {engagement.lead ?? '—'}</span>
          {engagement.priority !== 'normal' && (
            <span className="text-[var(--color-cord)]">优先级 {PRIORITY_LABEL[engagement.priority]}</span>
          )}
          <span>至 {formatDay(engagement.expiresAt)}</span>
        </div>

        <div className="mt-2 grid grid-cols-3 gap-1 border-t border-border pt-2 text-center">
          <MiniStat label="卡片" value={wallStats.cards} />
          <MiniStat label="红绳" value={wallStats.ropes} />
          <MiniStat label="图钉" value={wallStats.pins} />
        </div>

        <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>
            对象 {targets.length} · 线索 {entities.length}
          </span>
          {topRisk && topRisk.riskScore > 0 && (
            <Badge variant="outline" className={cn('border-transparent', RISK_BG[topRisk.riskLevel])}>
              最高风险 {topRisk.riskScore}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col items-center rounded-[var(--radius-sm)] bg-muted/60 py-1">
      <span className="font-mono text-[12px] font-semibold">{value}</span>
      <span className="text-[9px] text-muted-foreground">{label}</span>
    </div>
  );
}

/* ── 底部案情状态条 ─────────────────────────────────── */
export function WallStatusBar() {
  const controller = useCanvasStore(state => state.controller);
  const revision = useCanvasStore(state => state.revision);
  const entities = useStore(state => state.entities);
  const evidence = useStore(state => state.evidence);
  const timeline = useStore(state => state.timeline);
  const hypotheses = useStore(state => state.hypotheses);

  void revision;
  const hanging = useMemo(() => {
    if (!controller) return 0;
    return unplacedEntities(controller.surface.all(), entities).length;
  }, [controller, revision, entities]);

  const unverified = entities.filter(e => e.status === 'unverified').length;
  const openHypotheses = hypotheses.filter(h => h.status === 'open').length;

  return (
    <div className="flex items-center gap-1.5">
      <StatusChip icon={<RiBookmark3Line className="size-3" />} label="待上板" value={hanging} warn={hanging > 0} />
      <StatusChip icon={<RiShieldKeyholeLine className="size-3" />} label="待核实" value={unverified} warn={unverified > 0} />
      <StatusChip icon={<RiFlashlightLine className="size-3" />} label="待验证研判" value={openHypotheses} warn={openHypotheses > 0} />
      <StatusChip icon={<RiBookmark3Line className="size-3" />} label="证据" value={evidence.length} />
      <StatusChip icon={<RiTimeLine className="size-3" />} label="事件" value={timeline.length} />
    </div>
  );
}

function StatusChip({
  icon,
  label,
  value,
  warn,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  warn?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-border bg-card/90 px-2 py-[3px] backdrop-blur',
        warn && 'border-[var(--color-cord)]/30 text-[var(--color-cord)]'
      )}
    >
      {icon}
      <span className="opacity-70">{label}</span>
      <span className="font-mono font-medium">{value}</span>
    </span>
  );
}

/* ── 时间轴带 ───────────────────────────────────────── */
export function WallTimelineRibbon() {
  const timeline = useStore(state => state.timeline);
  const controller = useCanvasStore(state => state.controller);

  if (timeline.length === 0) return null;
  const events = [...timeline].sort(
    (a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime()
  );

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto px-1">
      <span className="shrink-0 text-[9.5px] uppercase tracking-wider text-muted-foreground">时间轴</span>
      {events.map(event => (
        <Hint
          key={event.id}
          label={`${TIMELINE_KIND_LABEL[event.kind]}　${event.description ?? ''}（双击钉到墙上）`}
          side="top"
        >
          <button
            type="button"
            onClick={() => pinEventToWall(controller, event.id)}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-card/90 px-2 py-[3px] text-[10px] backdrop-blur transition-colors hover:border-primary hover:text-primary"
          >
            <span className="size-1.5 shrink-0 rounded-full bg-[var(--color-assess)]" />
            <span className="font-mono text-[9px] text-muted-foreground">
              {new Date(event.occurredAt).toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' })}
            </span>
            <span className="max-w-[128px] truncate">{event.title}</span>
          </button>
        </Hint>
      ))}
    </div>
  );
}

/** 把时间线事件钉到墙面（已有则聚焦）。 */
function pinEventToWall(
  controller: Controller | null,
  eventId: string
): void {
  if (!controller) {
    if (eventId) useStore.getState().toast('info', '画布尚未就绪');
    return;
  }
  const event = useStore.getState().timeline.find(e => e.id === eventId);
  if (!event) return;
  const existing = controller.surface
    .all()
    .find(el => el.refKind === 'evidence' && el.refId === `timeline:${event.id}`);
  if (existing) {
    controller.selection.set([existing.id]);
    controller.viewport.setCenter([existing.x + existing.w / 2, existing.y + existing.h / 2]);
    controller.requestRender();
    useCanvasStore.getState().bump();
    return;
  }
  const el = controller.addNote([0, 0], {
    title: `${TIMELINE_KIND_LABEL[event.kind]}｜${event.title}`,
    text: [
      `时间：${formatDate(event.occurredAt)}`,
      event.location ? `地点：${event.location}` : '',
      event.description ?? '',
    ]
      .filter(Boolean)
      .join('\n'),
    color: 'orange',
    refKind: 'evidence',
    refId: `timeline:${event.id}`,
  });
  controller.selection.set([el.id]);
  useStore.getState().toast('success', '事件已钉到墙上');
}

/* ── AI 研判码头（继承研判能力到墙面）─────────────── */
export function WallAiDock() {
  const controller = useCanvasStore(state => state.controller);
  const aiProviders = useStore(state => state.aiProviders);
  const aiProviderId = useStore(state => state.aiProviderId);
  const aiModel = useStore(state => state.aiModel);
  const aiAnalysis = useStore(state => state.aiAnalysis);
  const activeTarget = useStore(state => state.targets.find(t => t.id === state.activeTargetId));
  const analyzeTarget = useStore(state => state.analyzeTarget);
  const planning = useStore(state => state.planningActions);
  const targets = useStore(state => state.targets);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    await analyzeTarget(activeTarget?.id ?? targets[0]?.id);
    setBusy(false);
  };

  /** 把当前研判结论钉到墙上。 */
  const pinAssessment = () => {
    if (!controller || !aiAnalysis) return;
    const center = controller.viewport.center;
    const lines = [
      aiAnalysis.profileSummary,
      '',
      ...aiAnalysis.attackSurface.slice(0, 4).map(item => `【${item.severity}】${item.title}：${item.detail}`),
      '',
      ...aiAnalysis.behaviorHypotheses.slice(0, 4).map(h => `推断 ${Math.round(h.confidence * 100)}%：${h.statement}`),
    ];
    controller.pushHistory();
    controller.addNote([center[0] - 120, center[1] - 80], {
      title: `研判摘要${activeTarget ? `｜${activeTarget.name}` : ''}`,
      text: lines.join('\n'),
      color: 'purple',
      refKind: activeTarget ? 'target' : undefined,
      refId: activeTarget?.id,
    });
    controller.addPin([center[0] - 120, center[1] - 80]);
    useStore.getState().toast('success', '研判结论已钉到关联墙');
  };

  return (
    <div className="pointer-events-auto flex items-center gap-1.5">
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2 py-[3px] text-[10px]',
          aiProviders.some(p => p.enabled)
            ? 'border-[var(--color-lead)]/30 bg-[var(--color-lead)]/8 text-[var(--color-lead)]'
            : 'border-border text-muted-foreground'
        )}
        title={
          aiProviders.some(p => p.enabled)
            ? `供应商 ${aiProviders.find(p => p.id === aiProviderId)?.name ?? '—'} · 模型 ${aiModel || '—'}`
            : '本地启发式引擎（未配置供应商）'
        }
      >
        <RiSparkling2Line className="size-3" />
        {aiProviders.some(p => p.enabled) ? aiModel || '远端模型' : '本地引擎'}
      </span>

      <Button size="xs" variant="outline" disabled={busy} onClick={run}>
        <RiFlashlightLine className={cn('size-3', busy && 'animate-pulse')} />
        {busy ? '研判中…' : '运行研判'}
      </Button>

      <Button size="xs" variant="ghost" disabled={!aiAnalysis} onClick={pinAssessment}>
        <RiPushpin2Line className="size-3" />
        研判上墙
      </Button>

      <Button
        size="xs"
        variant="ghost"
        title="让 AI 读现状并产出可执行动作"
        disabled={planning}
        onClick={() => useStore.getState().planActions()}
      >
        <RiSparkling2Line className={cn('size-3', planning && 'animate-pulse')} />
        操作台
      </Button>
    </div>
  );
}

/* ── 红绳建议 ───────────────────────────────────────── */
export function LinkSuggestions() {
  const controller = useCanvasStore(state => state.controller);
  const revision = useCanvasStore(state => state.revision);
  const entities = useStore(state => state.entities);
  const targets = useStore(state => state.targets);
  const relations = useStore(state => state.relations);
  const [open, setOpen] = useState(true);

  const suggestions = useMemo<LinkSuggestion[]>(() => {
    void revision;
    if (!controller) return [];
    return suggestLinks({
      wallElements: controller.surface.all(),
      entities,
      targets,
      relations,
    });
  }, [controller, revision, entities, targets, relations]);

  if (!controller || suggestions.length === 0) return null;

  const drawRope = (suggestion: LinkSuggestion, silent = false) => {
    controller.addRope(
      { id: suggestion.fromNode, anchor: [0.5, 0] },
      { id: suggestion.toNode, anchor: [0.5, 0] },
      suggestion.reason
    );
    if (!silent) {
      useStore
        .getState()
        .toast('success', `已拉绳：${suggestion.fromLabel} —${suggestion.reason}→ ${suggestion.toLabel}`);
    }
  };

  return (
    <div className="pointer-events-auto absolute left-[68px] top-[220px] z-10 w-[298px]">
      <div className="rounded-[var(--radius)] border border-border bg-card/96 shadow-[0_12px_36px_rgba(27,28,30,0.14)] backdrop-blur">
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="flex w-full items-center gap-1.5 px-3 py-2 text-left"
        >
          {open ? <RiArrowDownSLine className="size-3.5" /> : <RiArrowRightSLine className="size-3.5" />}
          <RiPinDistanceLine className="size-3.5 text-[var(--color-cord)]" />
          <span className="text-[11.5px] font-medium">红绳建议</span>
          <Badge variant="cord" className="ml-auto">
            {suggestions.length}
          </Badge>
        </button>

        {open && (
          <div className="border-t border-border p-2">
            <div className="flex max-h-[240px] flex-col gap-1 overflow-y-auto">
              {suggestions.map(suggestion => (
                <div
                  key={suggestion.key}
                  className="group flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-border bg-card px-2 py-1.5"
                >
                  <Badge variant="muted" className="shrink-0">
                    {suggestion.reason}
                  </Badge>
                  <div className="min-w-0 flex-1 truncate text-[10.5px]">
                    {suggestion.fromLabel}
                    <span className="mx-1 text-muted-foreground">→</span>
                    {suggestion.toLabel}
                  </div>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    className="shrink-0 text-muted-foreground hover:text-[var(--color-cord)]"
                    title="拉一条红绳"
                    onClick={() => drawRope(suggestion)}
                  >
                    <RiPinDistanceLine className="size-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              size="xs"
              variant="outline"
              className="mt-2 w-full"
              onClick={() => {
                controller.pushHistory();
                for (const suggestion of suggestions) drawRope(suggestion, true);
                useStore.getState().toast('success', `已按建议拉出 ${suggestions.length} 条红绳`);
              }}
            >
              <RiNodeTree className="size-3" />
              全部拉绳
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}